/**
 * Client-Side list Helper for Shopify
 * Manages localStorage state, count badges, and heart icon active classes dynamically.
 */
class ThemeWishlist {
  constructor() {
    this.STORAGE_KEY = 'theme_wishlist_ids';
    this.wishlist = this.getWishlist();
    this.init();
  }

  init() {
    this.updateBadges();
    this.updateButtons();
    this.bindEvents();

    // Listen for custom update events (supports cross-component sync)
    document.addEventListener('wishlist:updated', () => {
      this.wishlist = this.getWishlist();
      this.updateBadges();
      this.updateButtons();
    });
  }

  getWishlist() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to parse wishlist storage:', e);
      return [];
    }
  }

  saveWishlist(wishlist) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(wishlist));
      // Dispatch custom event to notify all components on the same tab
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    } catch (e) {
      console.error('Failed to save wishlist storage:', e);
    }
  }

  toggleProduct(productId) {
    if (!productId) return;
    productId = String(productId);

    let wishlist = this.getWishlist();
    const index = wishlist.indexOf(productId);

    if (index === -1) {
      wishlist.push(productId);
    } else {
      wishlist.splice(index, 1);
    }

    this.saveWishlist(wishlist);
  }

  hasProduct(productId) {
    if (!productId) return false;
    return this.wishlist.includes(String(productId));
  }

  updateBadges() {
    const count = this.wishlist.length;
    const badges = document.querySelectorAll('.wishlist-count-badge');

    badges.forEach(badge => {
      badge.textContent = count;
      if (count > 0) {
        badge.style.display = 'flex';
        badge.closest('.header-actions__wishlist-icon')?.classList.add('header-actions__wishlist-icon--has-items');
      } else {
        badge.style.display = 'none';
        badge.closest('.header-actions__wishlist-icon')?.classList.remove('header-actions__wishlist-icon--has-items');
      }
    });
  }

  updateButtons() {
    const buttons = document.querySelectorAll('.custom-wishlist-btn');

    buttons.forEach(button => {
      const productId = button.dataset.productId;
      if (productId) {
        const isWishlisted = this.hasProduct(productId);
        const svgPath = button.querySelector('path');

        if (isWishlisted) {
          button.classList.add('active');
          if (svgPath) {
            svgPath.setAttribute('fill', '#E11D48'); // Rich rose red fill when wishlisted
            svgPath.setAttribute('stroke', '#E11D48');
          }
        } else {
          button.classList.remove('active');
          if (svgPath) {
            svgPath.setAttribute('fill', 'transparent');
            svgPath.setAttribute('stroke', '#FFFFFF');
          }
        }
      }
    });
  }

  bindEvents() {
    // Event delegation on body to capture clicks on dynamic or AJAX product cards
    document.body.addEventListener('click', (e) => {
      const btn = e.target.closest('.custom-wishlist-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();

        const productId = btn.dataset.productId;
        if (productId) {
          this.toggleProduct(productId);

          // Trigger visual click pulse micro-animation
          btn.style.transform = 'scale(0.8)';
          setTimeout(() => {
            btn.style.transform = '';
          }, 150);
        }
      }
    });
  }
}

// Instantiate globally
if (!window.wishlistHelper) {
  window.wishlistHelper = new ThemeWishlist();
}
